/**
 * Base service class for all API services
 * Provides common functionality: abort controller, error handling, request management
 */

import apiClient from '@plugins/axios';
import type { HttpMethod, RequestConfig, ServiceResponse } from '@type/services';
import { getErrorMessage } from '@utils/error-utils';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

export abstract class BaseService {
  protected abortController: AbortController;
  protected readonly API_MONITOR = '/api-monitors';
  protected readonly DECISION_TABLE = '/decision-tables';
  protected readonly DECISION_TABLE_MOD = '/decision-table-mods';
  protected readonly DECISION_TREE = '/decision-trees';
  protected readonly DOCGEN_MONITOR = '/docgen-monitors';
  protected readonly IMAGE_STORE = '/image-stores';
  protected readonly LOAN_OFFERS = '/loan-offers';
  protected readonly LOAN_SERVICING = '/loan-servicing';
  protected readonly MIGRATION_LOG = '/migration-logs';
  protected readonly ROLE = '/roles';
  protected readonly SPREADING_CONFIG = '/spreading-configs';
  protected readonly SPREADING_CONFIG_MOD = '/spreading-config-mods';
  protected readonly SYSTEM_CONFIG = '/system-configs';
  protected readonly TENANT = '/tenants';
  protected readonly THEME_CONFIG = '/theme-config';
  protected readonly USER = '/users';

  constructor() {
    this.abortController = new AbortController();
  }

  /**
   * Generic request method returning ServiceResponse
   * Never throws - errors are returned as success: false with err
   *
   * @param config - Request configuration
   * @returns Promise resolving to ServiceResponse with typed payload or error
   */
  protected async request<TResponse = Record<string, unknown>>(
    config: RequestConfig
  ): Promise<ServiceResponse<TResponse>> {
    if (!this.abortController) this.abortController = new AbortController();

    try {
      const axiosConfig: AxiosRequestConfig = {
        ...this.axiosOptions(config.method),
        method: config.method,
        url: config.url,
        data: config.data,
        params: config.params,
        headers: config.headers,
        responseType: config.responseType || 'json',
      };

      const response: AxiosResponse<TResponse> = await apiClient.request(axiosConfig);
      return {
        success: true,
        response: response.data,
        originalResponse: response,
      };
    } catch (error) {
      const axiosError = error as AxiosError;

      return {
        success: false,
        response: null,
        originalResponse: axiosError.response,
        err:
          axiosError.name === 'AbortError' || axiosError.code === 'ERR_CANCELED'
            ? new Error('Request was cancelled')
            : new Error(getErrorMessage(axiosError)),
      };
    }
  }

  /**
   * Returns default axios options including abort signal
   * Can be extended in subclasses to add additional default options
   */
  axiosOptions(method: HttpMethod = 'GET') {
    const options: { signal?: AbortSignal } & Record<string, unknown> = {};
    if (['get'].includes(method.toLowerCase())) {
      options.signal = this.abortController.signal;
    }
    return options;
  }

  /**
   * Cancel ongoing request
   */
  cancelRequest(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
  }
}
